import { useCallback, useMemo } from "react";

import { Role } from "@ses-mams/api-contract";

import { tsr } from "~/utils/client";
import { useSearchFilters } from "./SearchUsersFilters";

type UseMemberPermissionsListDataParams = {
  searchValue: string;
  skip: number;
  take: number;
};

export function useMemberPermissionsListData({
  searchValue,
  skip,
  take,
}: UseMemberPermissionsListDataParams) {
  const { units, clusters, zones } = useSearchFilters();

  const unitIds = useMemo(() => units.map(unit => unit.id), [units]);
  const clusterIds = useMemo(
    () => clusters.map(cluster => cluster.id),
    [clusters]
  );
  const zoneIds = useMemo(() => zones.map(zone => zone.id), [zones]);

  const { data, isLoading, isRefetching, refetch } =
    tsr.admin.listMembers.useQuery({
      queryKey: [
        "members",
        searchValue,
        { unitIds, clusterIds, zoneIds },
        skip,
        take,
      ],
      queryData: {
        query: {
          skip,
          take,
          query: searchValue,
          unitIds,
          clusterIds,
          zoneIds,
        },
      },
    });

  return {
    isLoading,
    isRefetching,
    data: data?.body.items ?? [],
    totalCount: data?.body.totalCount ?? 0,
    refetch,
  };
}

export function useOverrideRolesOnMember() {
  const { mutateAsync, isPending } = tsr.admin.overrideRoles.useMutation();
  const queryClient = tsr.useQueryClient();

  const overrideRoles = useCallback(
    async (memberId: string, roles: Role[] | null) => {
      await mutateAsync({
        params: { memberId },
        body: { roles },
      });
      await queryClient.invalidateQueries({
        queryKey: ["members"],
      });
    },
    []
  );

  return { overrideRoles, isPending };
}
